import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import './Dashboard.css';

interface SavedSearch {
    id: number;
    query_string: string;
    doc_type: string | null;
    created_at: string;
}

interface Session {
    session_key: string;
    title: string | null;
    created_at: string;
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // Saved searches state
    const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
    const [searchPage, setSearchPage] = useState(1);
    const [hasMoreSearches, setHasMoreSearches] = useState(false);
    const [loadingSearches, setLoadingSearches] = useState(false);

    // Sessions state
    const [sessions, setSessions] = useState<Session[]>([]);
    const [sessionPage, setSessionPage] = useState(1);
    const [hasMoreSessions, setHasMoreSessions] = useState(false);
    const [loadingSessions, setLoadingSessions] = useState(false);

    // Chat access
    const [hasChat, setHasChat] = useState(true);
    // #101 — trial / expired state
    const [isTrial, setIsTrial] = useState(false);
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        fetchSavedSearches(1, true);
        fetchSessions(1, true);

        (async () => {
            try {
                const res = await api.get('/user/profile/me');
                if (res.data?.package?.has_chat === false) setHasChat(false);
                setIsTrial(!!res.data?.package?.is_test && !res.data?.is_expired);
                setIsExpired(!!res.data?.is_expired);
            } catch (err) {
                console.error('Failed to fetch profile', err);
            }
        })();
    }, []);

    const fetchSavedSearches = async (page: number, reset: boolean = false) => {
        setLoadingSearches(true);
        try {
            const res = await api.get('/saved-search', { params: { page, limit: 6 } });
            if (reset) {
                setSavedSearches(res.data.searches);
            } else {
                setSavedSearches(prev => [...prev, ...res.data.searches]);
            }
            setHasMoreSearches(res.data.has_more);
            setSearchPage(page);
        } catch (err) {
            console.error('Error fetching saved searches:', err);
        } finally {
            setLoadingSearches(false);
        }
    };

    const fetchSessions = async (page: number, reset: boolean = false) => {
        setLoadingSessions(true);
        try {
            const res = await api.get('/sessions', { params: { page, limit: 6 } });
            const sessionList = Array.isArray(res.data) ? res.data : (res.data.sessions || []);
            if (reset) {
                setSessions(sessionList);
            } else {
                setSessions(prev => [...prev, ...sessionList]);
            }
            // Check if there might be more
            setHasMoreSessions(sessionList.length === 6);
            setSessionPage(page);
        } catch (err) {
            console.error('Error fetching sessions:', err);
        } finally {
            setLoadingSessions(false);
        }
    };

    const handleDeleteSearch = async (id: number) => {
        if (!window.confirm(t('dashboard.confirmDelete', 'Supprimer cette recherche sauvegardée ?'))) return;
        try {
            await api.delete(`/saved-search/${id}`);
            setSavedSearches(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            console.error('Error deleting search:', err);
        }
    };

    const handleSearchClick = (queryString: string) => {
        navigate(`/${i18n.language}/search?${queryString}`);
    };

    const handleSessionClick = (sessionKey: string) => {
        navigate(`/${i18n.language}/chat?session=${sessionKey}`);
    };

    const getDocTypeLabel = (docType: string | null) => {
        const labels: { [key: string]: string } = {
            'jps': t('search.types.jps', 'Jurisprudence'),
            'code_article': t('search.types.code_article', 'Articles de Code'),
            'joarticles': t('search.types.joarticles', 'Textes officiels'),
            'theses': t('search.types.theses', 'Thèses'),
            'livres': t('search.types.livres', 'Livres')
        };
        return docType ? labels[docType] || docType : t('dashboard.allTypes', 'Tous types');
    };

    const getDocTypeBadgeClass = (docType: string | null) => {
        const classes: { [key: string]: string } = {
            'jps': 'badge-jps',
            'code_article': 'badge-code',
            'joarticles': 'badge-jo',
            'theses': 'badge-these',
            'livres': 'badge-livre'
        };
        return docType ? classes[docType] || 'badge-secondary' : 'badge-secondary';
    };

    const extractQueryPreview = (queryString: string) => {
        const params = new URLSearchParams(queryString);
        const q = params.get('q') || params.get('query');
        return q ? `"${q}"` : t('dashboard.noKeyword', 'Sans mot-clé');
    };

    // Truncate text helper
    const truncateText = (text: string, maxLength: number = 50) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="dashboard-page">
            <div className="container py-4">
                <div className="dashboard-header mb-4">
                    <h1 className="dashboard-title">
                        <i className="bi bi-speedometer2 me-3"></i>
                        {t('dashboard.title', 'Tableau de Bord')}
                    </h1>
                    <p className="text-muted">{t('dashboard.subtitle', 'Gérez vos recherches sauvegardées et vos sessions de chat')}</p>
                </div>

                {(isTrial || isExpired) && (
                    <div
                        className={`alert ${isExpired ? 'alert-danger' : 'alert-warning'} d-flex align-items-center justify-content-between flex-wrap gap-2`}
                        role="alert"
                    >
                        <div>
                            <i className={`bi ${isExpired ? 'bi-exclamation-circle-fill' : 'bi-info-circle-fill'} me-2`}></i>
                            {isExpired ? t('trial.dashboardAlertExpired') : t('trial.dashboardAlertTrial')}
                        </div>
                        <Link to={`/${i18n.language}/dashboard/subscription`} className="btn btn-primary btn-sm">
                            <i className="bi bi-credit-card me-1"></i>
                            {t('trial.subscribe')}
                        </Link>
                    </div>
                )}

                {/* Saved Searches Section */}
                <section className="dashboard-section mb-5">
                    <div className="section-header">
                        <h2 className="section-title">
                            <i className="bi bi-bookmark-star me-2 text-primary"></i>
                            {t('dashboard.savedSearches', 'Recherches Sauvegardées')}
                        </h2>
                        <span className="badge bg-primary rounded-pill">{savedSearches.length}</span>
                    </div>

                    {savedSearches.length === 0 && !loadingSearches ? (
                        <div className="empty-state">
                            <i className="bi bi-search empty-icon"></i>
                            <p>{t('dashboard.noSavedSearches', 'Aucune recherche sauvegardée')}</p>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate(`/${i18n.language}/search`)}
                            >
                                <i className="bi bi-plus me-1"></i>
                                {t('dashboard.startSearching', 'Commencer une recherche')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="cards-grid">
                                {savedSearches.map(search => (
                                    <div key={search.id} className="dashboard-card search-card">
                                        <div className="card-header-row">
                                            <span className={`type-badge ${getDocTypeBadgeClass(search.doc_type)}`}>
                                                {getDocTypeLabel(search.doc_type)}
                                            </span>
                                            <div className="card-actions">
                                                <button
                                                    className="btn-action btn-run"
                                                    onClick={() => handleSearchClick(search.query_string)}
                                                    title={t('dashboard.runSearch', 'Relancer')}
                                                >
                                                    <i className="bi bi-play-fill"></i>
                                                </button>
                                                <button
                                                    className="btn-action btn-delete"
                                                    onClick={() => handleDeleteSearch(search.id)}
                                                    title={t('dashboard.delete', 'Supprimer')}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            className="card-body-content"
                                            onClick={() => handleSearchClick(search.query_string)}
                                        >
                                            <p className="query-preview" title={extractQueryPreview(search.query_string)}>
                                                {truncateText(extractQueryPreview(search.query_string), 40)}
                                            </p>
                                            <div className="card-meta">
                                                <i className="bi bi-clock me-1"></i>
                                                {formatDate(search.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {hasMoreSearches && (
                                <div className="text-center mt-3">
                                    <button
                                        className="btn btn-outline-primary load-more-btn"
                                        onClick={() => fetchSavedSearches(searchPage + 1)}
                                        disabled={loadingSearches}
                                    >
                                        {loadingSearches ? (
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                        ) : (
                                            <i className="bi bi-arrow-down me-2"></i>
                                        )}
                                        {t('dashboard.loadMore', 'Charger plus')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* Sessions Section */}
                {hasChat && (
                    <section className="dashboard-section">
                        <div className="section-header">
                            <h2 className="section-title">
                                <i className="bi bi-chat-dots me-2 text-success"></i>
                                {t('dashboard.recentSessions', 'Sessions Récentes')}
                            </h2>
                            <span className="badge bg-success rounded-pill">{sessions.length}</span>
                        </div>

                        {sessions.length === 0 && !loadingSessions ? (
                            <div className="empty-state">
                                <i className="bi bi-chat empty-icon"></i>
                                <p>{t('dashboard.noSessions', 'Aucune session de chat')}</p>
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => navigate(`/${i18n.language}/chat`)}
                                >
                                    <i className="bi bi-plus me-1"></i>
                                    {t('dashboard.startChat', 'Nouvelle conversation')}
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="cards-grid">
                                    {sessions.map(session => (
                                        <div
                                            key={session.session_key}
                                            className="dashboard-card session-card"
                                            onClick={() => handleSessionClick(session.session_key)}
                                        >
                                            <div className="card-body-content">
                                                <h5 className="session-title" title={session.title || t('chat.newSession', 'Nouvelle session')}>
                                                    <i className="bi bi-chat-left-text me-2 text-success"></i>
                                                    {truncateText(session.title || t('chat.newSession', 'Nouvelle session'), 35)}
                                                </h5>
                                                <div className="card-meta">
                                                    <i className="bi bi-clock me-1"></i>
                                                    {session.created_at ? formatDate(session.created_at) : '-'}
                                                </div>
                                            </div>
                                            <div className="card-arrow">
                                                <i className="bi bi-chevron-right"></i>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {hasMoreSessions && (
                                    <div className="text-center mt-3">
                                        <button
                                            className="btn btn-outline-success load-more-btn"
                                            onClick={() => fetchSessions(sessionPage + 1)}
                                            disabled={loadingSessions}
                                        >
                                            {loadingSessions ? (
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                            ) : (
                                                <i className="bi bi-arrow-down me-2"></i>
                                            )}
                                            {t('dashboard.loadMore', 'Charger plus')}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
